import { useState, useMemo } from 'react';
import { Ruler, ArrowRightLeft } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToolUsage } from '@/hooks/useToolUsage';

type UnitCategory = {
  name: string;
  units: { id: string; label: string; toBase: (v: number) => number; fromBase: (v: number) => number }[];
};

const categories: UnitCategory[] = [
  {
    name: 'Length',
    units: [
      { id: 'm', label: 'Meters (m)', toBase: v => v, fromBase: v => v },
      { id: 'km', label: 'Kilometers (km)', toBase: v => v * 1000, fromBase: v => v / 1000 },
      { id: 'cm', label: 'Centimeters (cm)', toBase: v => v / 100, fromBase: v => v * 100 },
      { id: 'mm', label: 'Millimeters (mm)', toBase: v => v / 1000, fromBase: v => v * 1000 },
      { id: 'mi', label: 'Miles (mi)', toBase: v => v * 1609.344, fromBase: v => v / 1609.344 },
      { id: 'ft', label: 'Feet (ft)', toBase: v => v * 0.3048, fromBase: v => v / 0.3048 },
      { id: 'in', label: 'Inches (in)', toBase: v => v * 0.0254, fromBase: v => v / 0.0254 },
      { id: 'yd', label: 'Yards (yd)', toBase: v => v * 0.9144, fromBase: v => v / 0.9144 },
    ],
  },
  {
    name: 'Weight',
    units: [
      { id: 'kg', label: 'Kilograms (kg)', toBase: v => v, fromBase: v => v },
      { id: 'g', label: 'Grams (g)', toBase: v => v / 1000, fromBase: v => v * 1000 },
      { id: 'mg', label: 'Milligrams (mg)', toBase: v => v / 1e6, fromBase: v => v * 1e6 },
      { id: 'lb', label: 'Pounds (lb)', toBase: v => v * 0.453592, fromBase: v => v / 0.453592 },
      { id: 'oz', label: 'Ounces (oz)', toBase: v => v * 0.0283495, fromBase: v => v / 0.0283495 },
      { id: 't', label: 'Tonnes (t)', toBase: v => v * 1000, fromBase: v => v / 1000 },
    ],
  },
  {
    name: 'Temperature',
    units: [
      { id: 'c', label: 'Celsius (°C)', toBase: v => v, fromBase: v => v },
      { id: 'f', label: 'Fahrenheit (°F)', toBase: v => (v - 32) * 5 / 9, fromBase: v => v * 9 / 5 + 32 },
      { id: 'k', label: 'Kelvin (K)', toBase: v => v - 273.15, fromBase: v => v + 273.15 },
    ],
  },
  {
    name: 'Time',
    units: [
      { id: 's', label: 'Seconds', toBase: v => v, fromBase: v => v },
      { id: 'min', label: 'Minutes', toBase: v => v * 60, fromBase: v => v / 60 },
      { id: 'hr', label: 'Hours', toBase: v => v * 3600, fromBase: v => v / 3600 },
      { id: 'day', label: 'Days', toBase: v => v * 86400, fromBase: v => v / 86400 },
      { id: 'wk', label: 'Weeks', toBase: v => v * 604800, fromBase: v => v / 604800 },
    ],
  },
];

const UnitConverter = () => {
  useToolUsage('unit-converter');
  const [catIdx, setCatIdx] = useState(0);
  const [fromUnit, setFromUnit] = useState(categories[0].units[0].id);
  const [toUnit, setToUnit] = useState(categories[0].units[1].id);
  const [value, setValue] = useState('1');

  const cat = categories[catIdx];

  const result = useMemo(() => {
    const v = parseFloat(value);
    if (isNaN(v)) return '';
    const from = cat.units.find(u => u.id === fromUnit);
    const to = cat.units.find(u => u.id === toUnit);
    if (!from || !to) return '';
    const base = from.toBase(v);
    return to.fromBase(base).toLocaleString(undefined, { maximumFractionDigits: 8 });
  }, [value, fromUnit, toUnit, cat]);

  const swap = () => { setFromUnit(toUnit); setToUnit(fromUnit); };

  const changeCategory = (idx: number) => {
    setCatIdx(idx);
    setFromUnit(categories[idx].units[0].id);
    setToUnit(categories[idx].units[1].id);
    setValue('1');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <Ruler className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Unit Converter</h1>
          <p className="text-muted-foreground">Convert between length, weight, temperature, and time</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {categories.map((c, i) => (
          <Button key={c.name} size="sm" variant={catIdx === i ? 'default' : 'outline'} onClick={() => changeCategory(i)}>{c.name}</Button>
        ))}
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="grid sm:grid-cols-[1fr,auto,1fr] gap-4 items-end">
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">From</Label>
              <Select value={fromUnit} onValueChange={setFromUnit}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{cat.units.map(u => <SelectItem key={u.id} value={u.id}>{u.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" value={value} onChange={e => setValue(e.target.value)} placeholder="Enter value" className="text-lg" />
            </div>

            <Button variant="ghost" size="icon" onClick={swap} className="self-center">
              <ArrowRightLeft className="h-5 w-5" />
            </Button>

            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">To</Label>
              <Select value={toUnit} onValueChange={setToUnit}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{cat.units.map(u => <SelectItem key={u.id} value={u.id}>{u.label}</SelectItem>)}</SelectContent>
              </Select>
              <div className="h-10 flex items-center px-3 rounded-md border bg-muted/50 text-lg font-semibold text-primary">
                {result || '—'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UnitConverter;
